import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Receipt, Send, Check, Clock, AlertCircle, Eye, Trash2, Tag, Percent, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const navigate = useNavigate();

  // Generate month options (past 3 months + current + next 2 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -3; i <= 2; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthName = date.toLocaleString('en-GB', { month: 'long' });
      const lastDay = new Date(year, month + 1, 0).getDate();
      options.push({
        label: `${monthName} ${year}`,
        value: `${year}-${String(month + 1).padStart(2, '0')}`,
        start: `${year}-${String(month + 1).padStart(2, '0')}-01`,
        end: `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`
      });
    }
    return options;
  };

  const monthOptions = getMonthOptions();
  const currentMonthValue = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  const [generateForm, setGenerateForm] = useState({
    contract_id: searchParams.get('contract') || '',
    selected_month: currentMonthValue,
    period_start: '',
    period_end: '',
    work_days: '',  // e.g., "13, 19, 23, 29"
    line_item_quantities: {},
    invoice_extras: [],
    invoice_discounts: [],
    deposit_paid: ''  // Deposit already paid by client
  });
  const [generating, setGenerating] = useState(false);
  
  // For adding new extras/discounts
  const [newExtra, setNewExtra] = useState({ description: '', amount: '' });
  const [newDiscount, setNewDiscount] = useState({ description: '', amount: '' });

  useEffect(() => {
    fetchData();
    if (searchParams.get('contract')) {
      setShowGenerateForm(true);
    }
    // Set default month dates
    const currentMonth = monthOptions.find(m => m.value === currentMonthValue);
    if (currentMonth) {
      setGenerateForm(prev => ({
        ...prev,
        period_start: currentMonth.start,
        period_end: currentMonth.end
      }));
    }
  }, []);

  // When contract selection changes, load the contract's line items
  useEffect(() => {
    if (generateForm.contract_id) {
      const contract = contracts.find(c => c.id === generateForm.contract_id);
      setSelectedContract(contract || null);
      
      // Initialize line item quantities with defaults
      if (contract && contract.line_items) {
        const quantities = {};
        contract.line_items.forEach((item, idx) => {
          quantities[idx] = item.default_quantity || 1;
        });
        setGenerateForm(prev => ({ 
          ...prev, 
          line_item_quantities: quantities,
          invoice_extras: [],
          invoice_discounts: []
        }));
      }
    } else {
      setSelectedContract(null);
      setGenerateForm(prev => ({ 
        ...prev, 
        line_item_quantities: {},
        invoice_extras: [],
        invoice_discounts: []
      }));
    }
  }, [generateForm.contract_id, contracts]);

  const fetchData = async () => {
    try {
      const [invoicesRes, contractsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/invoices`),
        fetch(`${API_URL}/api/admin/contracts?status=active`)
      ]);
      const invoicesData = await invoicesRes.json();
      const contractsData = await contractsRes.json();
      setInvoices(invoicesData.invoices || []);
      setContracts(contractsData.contracts || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle month selection
  const handleMonthChange = (monthValue) => {
    const month = monthOptions.find(m => m.value === monthValue);
    if (month) {
      setGenerateForm(prev => ({
        ...prev,
        selected_month: monthValue,
        period_start: month.start,
        period_end: month.end
      }));
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contract_id: generateForm.contract_id,
          period_start: generateForm.period_start,
          period_end: generateForm.period_end,
          work_days: generateForm.work_days,
          line_item_quantities: generateForm.line_item_quantities,
          invoice_extras: generateForm.invoice_extras,
          invoice_discounts: generateForm.invoice_discounts,
          deposit_paid: parseFloat(generateForm.deposit_paid) || 0
        })
      });
      if (res.ok) {
        const data = await res.json();
        setShowGenerateForm(false);
        setSelectedContract(null);
        resetForm();
        fetchData();
        navigate(`/admin/invoices/${data.invoice.id}`);
      }
    } catch (err) {
      console.error('Failed to generate invoice:', err);
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    const currentMonth = monthOptions.find(m => m.value === currentMonthValue);
    setGenerateForm({
      contract_id: '',
      selected_month: currentMonthValue,
      period_start: currentMonth?.start || '',
      period_end: currentMonth?.end || '',
      work_days: '',
      line_item_quantities: {},
      invoice_extras: [],
      invoice_discounts: [],
      deposit_paid: ''
    });
    setNewExtra({ description: '', amount: '' });
    setNewDiscount({ description: '', amount: '' });
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/send`, { method: 'POST' });
      fetchData();
    } catch (err) {
      console.error('Failed to send invoice:', err);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/status?status=paid`, { method: 'PUT' });
      fetchData();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}`, { method: 'DELETE' });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Failed to delete invoice:', err);
    }
  };

  const updateLineItemQuantity = (index, quantity) => {
    setGenerateForm(prev => ({
      ...prev,
      line_item_quantities: {
        ...prev.line_item_quantities,
        [index]: parseInt(quantity) || 0
      }
    }));
  };

  // Add extra for this invoice
  const addExtra = () => {
    if (newExtra.description && newExtra.amount) {
      setGenerateForm(prev => ({
        ...prev,
        invoice_extras: [...prev.invoice_extras, {
          description: newExtra.description,
          amount: parseFloat(newExtra.amount)
        }]
      }));
      setNewExtra({ description: '', amount: '' });
    }
  };

  const removeExtra = (index) => {
    setGenerateForm(prev => ({
      ...prev,
      invoice_extras: prev.invoice_extras.filter((_, i) => i !== index)
    }));
  };

  // Add discount for this invoice
  const addDiscount = () => {
    if (newDiscount.description && newDiscount.amount) {
      setGenerateForm(prev => ({
        ...prev,
        invoice_discounts: [...prev.invoice_discounts, {
          description: newDiscount.description,
          amount: parseFloat(newDiscount.amount)
        }]
      }));
      setNewDiscount({ description: '', amount: '' });
    }
  };

  const removeDiscount = (index) => {
    setGenerateForm(prev => ({
      ...prev,
      invoice_discounts: prev.invoice_discounts.filter((_, i) => i !== index)
    }));
  };

  const calculatePreviewTotal = () => {
    if (!selectedContract) return 0;
    
    let total = selectedContract.monthly_price || 0;
    
    // Line items with quantities
    (selectedContract.line_items || []).forEach((item, idx) => {
      const qty = generateForm.line_item_quantities[idx] || item.default_quantity || 1;
      total += item.unit_price * qty;
    });
    
    // Contract's recurring additional charges
    (selectedContract.additional_charges || []).forEach(charge => {
      total += charge.amount;
    });

    // Invoice-specific extras
    generateForm.invoice_extras.forEach(extra => {
      total += extra.amount;
    });

    // Invoice-specific discounts (subtract)
    generateForm.invoice_discounts.forEach(discount => {
      total -= discount.amount;
    });
    
    // Add VAT if applicable
    if (selectedContract.vat_applicable) {
      const vatRate = selectedContract.vat_rate || 20;
      total = total * (1 + vatRate / 100);
    }
    
    return total;
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { bg: 'bg-gray-100 text-gray-600', icon: Clock },
      sent: { bg: 'bg-blue-100 text-blue-700', icon: Send },
      paid: { bg: 'bg-green-100 text-green-700', icon: Check },
      overdue: { bg: 'bg-red-100 text-red-700', icon: AlertCircle },
      cancelled: { bg: 'bg-gray-100 text-gray-500', icon: null }
    };
    const { bg, icon: Icon } = config[status] || config.draft;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bg}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
          <p className="text-gray-500 text-sm mt-1">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => setShowGenerateForm(!showGenerateForm)}
          className="bg-[#1a1f4e] hover:bg-[#2a2f5e]"
          data-testid="generate-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Generate Invoice
        </Button>
      </div>

      {/* Generate Invoice Form */}
      {showGenerateForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Generate Invoice</h2>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contract *</label>
              <select
                required
                value={generateForm.contract_id}
                onChange={(e) => setGenerateForm({ ...generateForm, contract_id: e.target.value })}
                className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none bg-white"
                data-testid="invoice-contract-select"
              >
                <option value="">Select a contract...</option>
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.site_name} - {contract.company_name} (£{contract.monthly_price?.toFixed(2) || '0.00'}/month)
                  </option>
                ))}
              </select>
              {contracts.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  No active contracts. <Link to="/admin/contracts" className="text-[#1a1f4e] hover:underline">Create a contract first</Link>
                </p>
              )}
            </div>

            {/* Month Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Invoice Month *
              </label>
              <select
                required
                value={generateForm.selected_month}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none bg-white text-lg font-medium"
                data-testid="invoice-month-select"
              >
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Period: {generateForm.period_start} to {generateForm.period_end}
              </p>
            </div>

            {/* Work Days Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Days Works Carried Out (optional)
              </label>
              <input
                type="text"
                placeholder="e.g., 13, 19, 23, 29"
                value={generateForm.work_days}
                onChange={(e) => setGenerateForm({ ...generateForm, work_days: e.target.value })}
                className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none"
                data-testid="invoice-work-days"
              />
              <p className="text-xs text-gray-500 mt-1">
                {generateForm.work_days 
                  ? `Will show: "For works carried out on ${generateForm.work_days.split(',').map(d => d.trim()).filter(d => d).map(d => {
                      const num = parseInt(d);
                      const suffix = num === 1 || num === 21 || num === 31 ? 'st' : num === 2 || num === 22 ? 'nd' : num === 3 || num === 23 ? 'rd' : 'th';
                      return num + suffix;
                    }).join(', ')} ${monthOptions.find(m => m.value === generateForm.selected_month)?.label || ''}"`
                  : 'Leave blank for "For works carried out in [Month Year]"'
                }
              </p>
            </div>

            {/* Line Items with Quantity Inputs */}
            {selectedContract && selectedContract.line_items && selectedContract.line_items.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">Price Tiers / Line Items</label>
                <p className="text-xs text-gray-500 mb-3">Adjust quantities for this invoice period</p>
                
                <div className="space-y-3">
                  {selectedContract.line_items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 bg-blue-50 rounded-lg p-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.description}</p>
                        <p className="text-xs text-gray-500">£{item.unit_price.toFixed(2)} each</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500">Qty:</label>
                        <input
                          type="number"
                          min="0"
                          value={generateForm.line_item_quantities[idx] ?? item.default_quantity ?? 1}
                          onChange={(e) => updateLineItemQuantity(idx, e.target.value)}
                          className="w-20 h-9 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm text-center"
                          data-testid={`line-item-qty-${idx}`}
                        />
                      </div>
                      <div className="w-24 text-right">
                        <p className="text-sm font-semibold text-blue-700">
                          £{(item.unit_price * (generateForm.line_item_quantities[idx] ?? item.default_quantity ?? 1)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Extras Section - for this invoice only */}
            {selectedContract && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-green-600" />
                  <label className="text-sm font-medium text-gray-700">Extras (this invoice only)</label>
                </div>
                <p className="text-xs text-gray-500 mb-3">Add one-time charges for this period (e.g., extra cleaning, supplies used)</p>
                
                {generateForm.invoice_extras.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {generateForm.invoice_extras.map((extra, index) => (
                      <div key={index} className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                        <span className="flex-1 text-sm">{extra.description}</span>
                        <span className="text-sm font-medium text-green-700">+£{extra.amount.toFixed(2)}</span>
                        <button type="button" onClick={() => removeExtra(index)} className="text-gray-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Description (e.g., Extra deep clean)"
                    value={newExtra.description}
                    onChange={(e) => setNewExtra({ ...newExtra, description: e.target.value })}
                    className="flex-1 h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="£"
                    value={newExtra.amount}
                    onChange={(e) => setNewExtra({ ...newExtra, amount: e.target.value })}
                    className="w-24 h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addExtra} disabled={!newExtra.description || !newExtra.amount}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Discounts Section - for this invoice only */}
            {selectedContract && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Percent className="w-4 h-4 text-orange-600" />
                  <label className="text-sm font-medium text-gray-700">Discounts (this invoice only)</label>
                </div>
                <p className="text-xs text-gray-500 mb-3">Add discounts or credits for this period</p>
                
                {generateForm.invoice_discounts.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {generateForm.invoice_discounts.map((discount, index) => (
                      <div key={index} className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-2">
                        <span className="flex-1 text-sm">{discount.description}</span>
                        <span className="text-sm font-medium text-orange-700">-£{discount.amount.toFixed(2)}</span>
                        <button type="button" onClick={() => removeDiscount(index)} className="text-gray-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Description (e.g., Loyalty discount)"
                    value={newDiscount.description}
                    onChange={(e) => setNewDiscount({ ...newDiscount, description: e.target.value })}
                    className="flex-1 h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="£"
                    value={newDiscount.amount}
                    onChange={(e) => setNewDiscount({ ...newDiscount, amount: e.target.value })}
                    className="w-24 h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addDiscount} disabled={!newDiscount.description || !newDiscount.amount}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Deposit Already Paid */}
            {selectedContract && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-green-600" />
                  <label className="text-sm font-medium text-gray-700">Deposit Already Paid (optional)</label>
                </div>
                <p className="text-xs text-gray-500 mb-2">If the client has paid a deposit, enter the amount here. It will be deducted from the total on the invoice.</p>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">£</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={generateForm.deposit_paid}
                    onChange={(e) => setGenerateForm({ ...generateForm, deposit_paid: e.target.value })}
                    className="w-32 h-10 px-3 rounded-lg border border-gray-200 focus:border-green-500 outline-none text-sm"
                    data-testid="deposit-paid-input"
                  />
                </div>
              </div>
            )}

            {/* Preview Total */}
            {selectedContract && (
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                {parseFloat(generateForm.deposit_paid) > 0 ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-500">Invoice Total</p>
                      <p className="text-lg font-semibold text-gray-700">£{calculatePreviewTotal().toFixed(2)}</p>
                    </div>
                    <div className="flex justify-between items-center text-green-600">
                      <p className="text-sm">Less: Deposit Paid</p>
                      <p className="text-lg font-semibold">-£{parseFloat(generateForm.deposit_paid).toFixed(2)}</p>
                    </div>
                    <div className="border-t border-gray-300 pt-2 flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">Balance Due</p>
                        {selectedContract.vat_applicable && (
                          <p className="text-xs text-gray-400">Including VAT ({selectedContract.vat_rate || 20}%)</p>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-[#1a1f4e]">£{(calculatePreviewTotal() - parseFloat(generateForm.deposit_paid)).toFixed(2)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">Preview Total</p>
                      {selectedContract.vat_applicable && (
                        <p className="text-xs text-gray-400">Including VAT ({selectedContract.vat_rate || 20}%)</p>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-[#1a1f4e]">£{calculatePreviewTotal().toFixed(2)}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={generating || !generateForm.contract_id} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="create-invoice-btn">
                {generating ? 'Generating...' : 'Generate Invoice'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowGenerateForm(false); setSelectedContract(null); resetForm(); }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* Invoices List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {invoices.length === 0 ? (
          <div className="p-8 text-center">
            <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No invoices yet. Generate your first invoice from an active contract.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Client / Site</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/admin/invoices/${invoice.id}`} className="font-medium text-[#1a1f4e] hover:underline">
                      {invoice.invoice_number}
                    </Link>
                    <p className="text-xs text-gray-500">{formatDate(invoice.invoice_date)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{invoice.client_company_name}</p>
                    <p className="text-xs text-gray-500">{invoice.site_name}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-600">{invoice.period_start} — {invoice.period_end}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-sm font-semibold">£{invoice.total_amount.toFixed(2)}</p>
                    {invoice.vat_applicable && (
                      <p className="text-xs text-gray-500">inc. VAT</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/invoices/${invoice.id}`} className="p-2 text-gray-400 hover:text-gray-600" title="View">
                        <Eye className="w-4 h-4" />
                      </Link>
                      {invoice.status === 'draft' && (
                        <button
                          onClick={() => handleSendInvoice(invoice.id)}
                          className="p-2 text-gray-400 hover:text-blue-600"
                          title="Send Invoice"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                        <button
                          onClick={() => handleMarkPaid(invoice.id)}
                          className="p-2 text-gray-400 hover:text-green-600"
                          title="Mark as Paid"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete Invoice"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InvoiceList;
